import React from 'react';
import { useEffect, useMemo, useState, useContext } from 'react';
import './home.css';
import { UserContext } from '../context/UserContext';
import { CatalogAPI } from '../api';

export default function PublicCatalog() {
  const { user } = useContext(UserContext);
  const [data, setData] = useState({ services: [], packages: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showGross, setShowGross] = useState(false);
  const [expanded, setExpanded] = useState({}); // serviceId -> bool

  // Compute services sorted by how many packages include them (desc), then by name
  const sortedServices = useMemo(() => {
    const counts = new Map(); // serviceId -> count of packages containing it
    for (const pkg of (data.packages || [])) {
      for (const s of (pkg.Services || [])) {
        counts.set(s.id, (counts.get(s.id) || 0) + 1);
      }
    }
    const list = [...(data.services || [])];
    list.sort((a, b) => {
      const ca = counts.get(a.id) || 0;
      const cb = counts.get(b.id) || 0;
      if (cb !== ca) return cb - ca; // more included first
      // tie-breaker by name
      const an = (a.name || '').toLowerCase();
      const bn = (b.name || '').toLowerCase();
      if (an < bn) return -1;
      if (an > bn) return 1;
      return 0;
    });
    return list;
  }, [data.services, data.packages]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const res = await CatalogAPI.get();
        if (!mounted) return;
        setData({ services: res.services || [], packages: res.packages || [] });
      } catch (e) {
        if (!mounted) return;
        setError(e?.response?.data?.error || e.message);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const VAT = 0.19;
  const fmt = (v) => Number(v).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
  const applyVat = (v) => showGross ? v * (1 + VAT) : v;
  const formatPrice = (n) => {
    if (n == null) return '—';
    const v = Number(n);
    if (!isFinite(v)) return String(n);
    return fmt(applyVat(v));
  };
  const toNum = (v, fb=0) => Number.isFinite(Number(v)) ? Number(v) : fb;

  // Discount helpers
  const svcDiscountMap = useMemo(() => {
    const m = new Map();
    for (const s of (data.services || [])) {
      m.set(s.id, Number(s.effectiveDiscountPct || 0));
    }
    return m;
  }, [data.services]);
  const applyPct = (v, pct) => v * (1 - (Number(pct || 0) / 100));
  const formatDiscountPair = (baseNet, pct, opts={ strong:true }) => {
    if (baseNet == null) return '—';
    const base = Number(baseNet);
    if (!isFinite(base)) return String(baseNet);
    const has = Number(pct) > 0;
    const discounted = has ? applyPct(base, pct) : base;
    if (!has) return <span>{fmt(applyVat(base))}</span>;
    return (
      <span style={{ display:'inline-flex', flexDirection:'column', alignItems:'center', gap:2 }}>
        <span style={{ textDecoration:'line-through', color:'#999' }}>{fmt(applyVat(base))}</span>
        <span style={{ fontWeight: opts.strong ? 1000 : 600, color:'#22c55e' }}>{fmt(applyVat(discounted))}</span>
        <span style={{ fontSize:11, color:'#9FE870', background:'rgba(34,197,94,0.15)', border:'1px solid rgba(34,197,94,0.35)', padding:'1px 6px', borderRadius:9999 }}>Aktion −{Number(pct).toFixed(0)}%</span>
      </span>
    );
  };

  // Sort packages by lowest monthly price (ascending) for left-to-right order
  const sortedPackages = useMemo(() => {
    const list = [...(data.packages || [])];
    list.sort((a, b) => toNum(a.monthlyNetPrice, 0) - toNum(b.monthlyNetPrice, 0));
    return list;
  }, [data.packages]);

  const annualPriceRaw = (pkg) => toNum(pkg.monthlyNetPrice, 0) * toNum(pkg.monthsBilledPerYear, 12);
  const annualPrice = (pkg) => applyVat(annualPriceRaw(pkg));
  const packageValueRaw = (pkg) => {
    const list = pkg.Services || [];
    let sum = 0;
    for (const s of list) {
      const base = toNum(s.netPrice, 0);
      const svcPct = svcDiscountMap.get(s.id) || 0;
      const price = applyPct(base, svcPct);
      const q = (s?.PackageService?.quantity == null || s.PackageService.quantity === '') ? 1 : toNum(s.PackageService.quantity, 1);
      const freq = s?.PackageService?.frequency || 'per_month';
      const mult = freq === 'per_month' || freq === 'unlimited' ? 12 : (freq === 'per_year' ? 1 : 0);
      sum += price * q * mult;
    }
    return sum;
  };
  const packageValue = (pkg) => applyVat(packageValueRaw(pkg));
  const discountedAnnualRaw = (pkg) => {
    const pct = Number(pkg.effectiveDiscountPct || 0);
    const base = annualPriceRaw(pkg);
    return applyPct(base, pct);
  };
  const savingsRaw = (pkg) => packageValueRaw(pkg) - discountedAnnualRaw(pkg);
  const savings = (pkg) => applyVat(savingsRaw(pkg));

  return (
    <div className="home-page">
      <section className={user ? 'hero-offset-if-sidebar' : ''} style={{ width:'100vw', margin:0, padding:0 }}>
        <h2 className="hero-title" style={{ textAlign:'center', margin:'0 0 8px' }}>Angebote & Leistungen</h2>
        <p className="hero-desc" style={{ margin:'0 auto 12px', maxWidth:760, textAlign:'center' }}>Alle Dienstleistungen und Abo-Pakete im Überblick.</p>
        {/* CTA moved below the intro text block */}
      </section>
      <section style={{ width:'100%', maxWidth:1100, margin:'0 auto', padding:'0 16px 40px 16px', color:'#fff' }}>
        <div style={{ display:'flex', justifyContent:'flex-end', alignItems:'center', gap:12, marginBottom:10 }}>
          <label style={{ display:'inline-flex', alignItems:'center', gap:8, fontSize:14 }}>
            <input type="checkbox" checked={showGross} onChange={e => setShowGross(e.target.checked)} />
            Bruttopreise anzeigen (inkl. 19% MwSt.)
          </label>
        </div>
        {loading && <div>Lade …</div>}
        {error && <div style={{ color:'#b00' }}>Fehler: {String(error)}</div>}
        {!loading && !error && (
          <>
            <div style={{
              background:'rgba(255,255,255,0.03)',
              border:'1px solid rgba(255,255,255,0.08)',
              borderRadius:10,
              padding:'14px 16px',
              margin:'8px 0 18px 0',
              lineHeight:1.55,
              color:'#ddd'
            }}>
              <p style={{ margin:'0 0 8px 0' }}>
                Du kannst alle Services & Dienstleistungen separat, einmalig oder fortlaufend als Abo buchen – so nutzt du genau das, was du brauchst, und kannst dabei zusätzliche Kosten sparen.
              </p>
              <p style={{ margin:'0 0 8px 0' }}>
                Die Services & Dienstleistungen des <span style={{ fontWeight:700 }}>AP‑Network</span> sind in {data.packages.length} {data.packages.length === 1 ? 'Standard‑Paket' : 'Standard‑Pakete'} unterteilt.
              </p>
              <p style={{ margin:0, color:'#ccc' }}>
                Auf Anfrage schnüren wir auch gerne individuelle Pakete – maßgeschneidert auf deine Bedürfnisse. Eine detaillierte Auflistung aller Services & Dienstleistungen inkl. Abo‑Paketen und Preisen siehst du im Überblick hier unten.
              </p>
            </div>
            {/* Prominent CTA directly under description and above the table */}
            <div style={{ display:'flex', justifyContent:'center', margin:'6px 0 18px 0' }}>
              <a href="/angebot-anfordern" className="hero-btn-primary">Individuelles Angebot anfordern</a>
            </div>
            {data.services.length === 0 ? (
              <div style={{ opacity:.8 }}>Keine Dienstleistungen verfügbar.</div>
            ) : (
              <>
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', minWidth: 780 }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign:'left', borderBottom:'1px solid rgba(255,255,255,0.2)', padding:'6px 8px', verticalAlign:'top' }}>
                        <div style={{ fontWeight:700 }}>
                          Dienstleistung
                          <sup style={{ marginLeft:6, color:'#ccc' }}>⁴</sup>
                        </div>
                      </th>
                      <th style={{ textAlign:'center', borderBottom:'1px solid rgba(255,255,255,0.2)', padding:'6px 8px', verticalAlign:'top' }}>
                        <div style={{ fontWeight:700 }}>{`Preis bei Einzelbuchung (${showGross ? 'brutto' : 'netto'})`}</div>
                      </th>
                      {sortedPackages.map(pkg => (
                        <th key={pkg.id} style={{ textAlign:'left', borderBottom:'1px solid rgba(255,255,255,0.2)', padding:'6px 8px', verticalAlign:'top' }}>
                          <div style={{ fontWeight:700 }} title={pkg.shortDescription || ''}>
                            {pkg.name}
                            <sup style={{ marginLeft:6, color:'#ccc' }}>¹</sup>
                          </div>
                          {toNum(pkg.monthlyNetPrice, 0) <= 0 ? (
                            <>
                              <div title={pkg.shortDescription || ''}
                                   style={{
                                     marginTop:6,
                                     display:'inline-block',
                                     padding:'6px 10px',
                                     borderRadius:8,
                                     background:'linear-gradient(90deg, rgba(255,215,128,0.18), rgba(255,215,128,0.06))',
                                     border:'1px solid rgba(255,215,128,0.35)',
                                     color:'#f5d97a',
                                     fontWeight:800,
                                     letterSpacing:0.2
                                   }}>
                                Kostenlos
                              </div>
                              <div style={{ fontSize:11, color:'#e9d8a6', marginTop:6 }} title={pkg.shortDescription || ''}>
                                Automatisch in der <span style={{ fontWeight:700 }}>AP‑Network</span> Mitgliedschaft enthalten.
                              </div>
                            </>
                          ) : (
                            <>
                              <div style={{ fontSize:13, color:'#bbb', display:'flex', flexDirection:'column', alignItems:'flex-start', gap:2 }} title={pkg.shortDescription || ''}>
                                {Number(pkg.effectiveDiscountPct||0) > 0 ? (
                                  <>
                                    <span style={{ textDecoration:'line-through', color:'#999' }}>{formatPrice(pkg.monthlyNetPrice)} / Monat</span>
                                    <span style={{ fontWeight:1000, color:'#22c55e' }}>{fmt(applyVat(applyPct(toNum(pkg.monthlyNetPrice,0), pkg.effectiveDiscountPct)))} / Monat</span>
                                    <span style={{ fontSize:11, color:'#9FE870', background:'rgba(34,197,94,0.15)', border:'1px solid rgba(34,197,94,0.35)', padding:'1px 6px', borderRadius:9999 }}>Aktion −{Number(pkg.effectiveDiscountPct).toFixed(0)}%</span>
                                  </>
                                ) : (
                                  <span style={{ fontWeight:1000 }}>{formatPrice(pkg.monthlyNetPrice)} / Monat</span>
                                )}
                              </div>
                              <div style={{ fontSize:11, color:'#999' }} title={pkg.shortDescription || ''}>bei monatlicher Zahlung</div>
                              <div style={{ fontSize:12, color:'#aaa' }} title={pkg.shortDescription || ''}>
                                Jahrespreis (bei jährlicher Zahlung)
                                <sup style={{ marginLeft:6, color:'#ccc' }}>²</sup>
                                : {Number(pkg.effectiveDiscountPct||0) > 0 ? (
                                  <>
                                    <span style={{ textDecoration:'line-through', color:'#999', marginRight:6 }}>{formatPrice(annualPriceRaw(pkg))}</span>
                                    <span style={{ fontWeight:700, color:'#22c55e' }}>{fmt(applyVat(discountedAnnualRaw(pkg)))}</span>
                                  </>
                                ) : (
                                  formatPrice(annualPriceRaw(pkg))
                                )}
                              </div>
                              <div style={{ fontSize:12, color: ((Math.round(savingsRaw(pkg) * 100) / 100) > 0 ? '#22c55e' : '#22c55e'), fontWeight: 700 }}>
                                Ersparnis<sup style={{ marginLeft:6, color:'#ccc' }}>³</sup>: {formatPrice(savingsRaw(pkg))}
                              </div>
                            </>
                          )}
                        </th>
                      ))}
                    </tr>
                    <tr>
                      <th style={{ textAlign:'left', borderBottom:'1px solid rgba(255,255,255,0.2)', padding:'6px 8px', verticalAlign:'top' }}>
                        <div style={{ fontSize:12, color:'#ccc', whiteSpace:'pre-wrap' }}>
                          Tipp: Klicke auf eine Dienstleistung, um die ausführliche Beschreibung und weitere Hinweise aufzuklappen.
                        </div>
                      </th>
                      <th style={{ textAlign:'left', borderBottom:'1px solid rgba(255,255,255,0.2)', padding:'6px 8px', verticalAlign:'top' }}>
                        <div style={{ fontSize:12, color:'#ccc', whiteSpace:'pre-wrap' }}>
                          Für alle, die gezielt einzelne Spezialservices buchen – wenn du nur genau diesen einen Baustein brauchst, ohne in ein ganzes Paket investieren zu müssen oder um dein bestehendes Abo punktuell zu ergänzen.
                        </div>
                      </th>
                      {sortedPackages.map(pkg => (
                        <th key={pkg.id+':info'} style={{ textAlign:'left', borderBottom:'1px solid rgba(255,255,255,0.2)', padding:'6px 8px', verticalAlign:'top' }}>
                          {(pkg.longDescription || pkg.note) && (
                            <div style={{ fontSize:12, color:'#ccc', whiteSpace:'pre-wrap' }}>
                              {pkg.longDescription || pkg.note}
                            </div>
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedServices.map((svc, idx) => (
                      <React.Fragment key={svc.id}>
                        <tr key={svc.id}
                            onClick={() => setExpanded(e => ({ ...e, [svc.id]: !e[svc.id] }))}
                            title={svc.shortDescription || ''}
                            style={{ cursor:'pointer', borderBottom: (!expanded[svc.id] && idx !== sortedServices.length - 1) ? '1px solid rgba(255,255,255,0.07)' : 'none' }}>
                          <td style={{ padding:'6px 8px' }}>
                            <span style={{ display:'inline-flex', alignItems:'center', gap:6 }}>
                              <span style={{fontWeight:600}}>{svc.name}</span>
                              <span style={{fontSize:12, opacity:.7}}>{expanded[svc.id] ? '▲' : '▼'}</span>
                            </span>
                          </td>
                          <td style={{ padding:'6px 8px', textAlign:'center' }}>
                            {formatDiscountPair(svc.netPrice, svc.effectiveDiscountPct, { strong: true })}
                          </td>
                          {sortedPackages.map(pkg => {
                            const found = (pkg.Services || []).find(s => s.id === svc.id);
                            if (!found) return (
                              <td key={pkg.id+':'+svc.id} style={{ padding:'6px 8px', textAlign:'center' }}>
                                <span style={{ fontSize:16, fontWeight:600 }} aria-label="Nicht enthalten" title="Nicht enthalten">❌</span>
                              </td>
                            );
                            const info = (found.PackageService?.info || '').toString().trim();
                            return (
                              <td key={pkg.id+':'+svc.id} style={{ padding:'6px 8px', textAlign:'center' }}>
                                <span style={{ fontSize:16 }} aria-label="Enthalten" title="Enthalten">✅</span>
                                {info && (
                                  <div style={{ fontSize:11, color:'#bbb', marginTop:4, whiteSpace:'pre-wrap', textAlign:'center' }}>
                                    {info}
                                  </div>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                        {expanded[svc.id] && (
                          <tr key={svc.id+':details'} style={{ background:'rgba(255,255,255,0.03)', borderBottom: (idx !== sortedServices.length - 1) ? '1px solid rgba(255,255,255,0.07)' : 'none' }}>
                            <td colSpan={2 + sortedPackages.length} style={{ padding:'10px 12px', whiteSpace:'pre-wrap' }}>
                              {(svc.longDescription || '').trim() ? (
                                <div style={{ marginBottom: svc.note ? 10 : 0 }}>
                                  {svc.longDescription}
                                </div>
                              ) : null}
                              {svc.note && (
                                <div style={{ fontSize:12, lineHeight:1.5 }}>
                                  <span style={{
                                    display:'inline-block',
                                    fontSize:10,
                                    letterSpacing:0.5,
                                    fontWeight:700,
                                    textTransform:'uppercase',
                                    color:'#999',
                                    background:'rgba(255,255,255,0.06)',
                                    padding:'2px 6px',
                                    borderRadius:4,
                                    marginRight:8
                                  }}>Hinweis</span>
                                  <span style={{ color:'#bbb' }}>{svc.note}</span>
                                </div>
                              )}
                              {!(svc.longDescription || svc.note) && (
                                <div style={{ color:'#777' }}>Keine weiteren Informationen.</div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{
                marginTop: 14,
                background: 'rgba(255,255,255,0.02)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 10,
                padding: '10px 12px',
                color: '#bbb',
                fontSize: 12,
                lineHeight: 1.55
              }}>
                <div style={{ display:'flex', gap:8 }}>
                  <span style={{ minWidth:18, textAlign:'right', color:'#ccc' }}>¹</span>
                  <span>Die Mindestvertragslaufzeit beträgt 12 Monate, danach ist monatlich kündbar.</span>
                </div>
                <div style={{ display:'flex', gap:8, marginTop:6 }}>
                  <span style={{ minWidth:18, textAlign:'right', color:'#ccc' }}>²</span>
                  <span>Der Jahrespreis bei jährlicher Zahlung enthält einen Rabatt; dieser ist nur im 1. Jahr gültig. Danach wird der reguläre Monatspreis × 12 berechnet.</span>
                </div>
                <div style={{ display:'flex', gap:8, marginTop:6 }}>
                  <span style={{ minWidth:18, textAlign:'right', color:'#ccc' }}>³</span>
                  <span>Summe der jeweils im Paket enthaltenen Dienstleistungen zum Einzelpreis (bei monatlicher Beauftragung) abzüglich des Paket‑Jahrespreises. So siehst du auf einen Blick, wie viel günstiger du mit unseren Kombi‑Abos fährst.</span>
                </div>
                <div style={{ display:'flex', gap:8, marginTop:6 }}>
                  <span style={{ minWidth:18, textAlign:'right', color:'#ccc' }}>⁴</span>
                  <span>Bei einigen Services und Dienstleistungen können zusätzliche Kosten anfallen, die nicht im Paketpreis enthalten sind.</span>
                </div>
              </div>
                {/* Bottom CTA below the footnotes */}
                <div style={{ display:'flex', justifyContent:'center', margin:'20px 0 0' }}>
                  <a href="/angebot-anfordern" className="hero-btn-primary">Individuelles Angebot anfordern</a>
                </div>
              </>
            )}
          </>
        )}
      </section>
    </div>
  );
}
